import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';

const initialState = {
    isLoading: false,
    error: null,
    files: { count: 0, results: [] },
    stats: { total: 0, linked: 0, thisMonth: 0, autoUpload: false },
    currentFile: null,
    // Lead-scoped file list (for LeadFormsTab)
    leadFiles: { count: 0, results: [] },
    leadFilesLoading: false,
};

const slice = createSlice({
    name: 'storage',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
            state.error = null;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        setFiles(state, action) {
            state.isLoading = false;
            state.files = action.payload;
        },
        setStats(state, action) {
            state.stats = action.payload;
        },
        setCurrentFile(state, action) {
            state.currentFile = action.payload;
        },
        clearCurrentFile(state) {
            state.currentFile = null;
        },
        startLeadFilesLoading(state) {
            state.leadFilesLoading = true;
        },
        setLeadFiles(state, action) {
            state.leadFilesLoading = false;
            state.leadFiles = action.payload;
        },
        clearLeadFiles(state) {
            state.leadFiles = { count: 0, results: [] };
            state.leadFilesLoading = false;
        },
    },
});

export default slice.reducer;

export const { clearCurrentFile, clearLeadFiles } = slice.actions;

// ─── Thunks ─────────────────────────────────────────────────────────────────

/**
 * Fetch paginated, filterable file list for a practice.
 * Endpoint: GET /api/v1/practices/{practiceId}/storage/files/
 */
export function getStorageFiles(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const sp = new URLSearchParams();
            sp.set('ordering', params.ordering || '-created_at');
            if (params.limit) sp.set('limit', params.limit);
            if (params.offset !== undefined) sp.set('offset', params.offset);
            if (params.search) sp.set('search', params.search);
            if (params.form_type) sp.set('form_type', params.form_type);
            if (params.lead_id) sp.set('lead', params.lead_id);
            if (params.source) sp.set('source', params.source);
            if (params.cloud_sync_status) sp.set('cloud_sync_status', params.cloud_sync_status);
            if (params.created_after) sp.set('created_after', params.created_after);
            if (params.created_before) sp.set('created_before', params.created_before);

            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/files/?${sp.toString()}`
            );
            if (res.status === 200) {
                dispatch(slice.actions.setFiles(res.data));
            } else {
                // 404 is expected when backend isn't ready yet
                dispatch(slice.actions.setFiles({ count: 0, results: [] }));
            }
        } catch (error) {
            dispatch(slice.actions.setFiles({ count: 0, results: [] }));
        }
    };
}

/**
 * Fetch aggregate stats for the practice storage.
 * Endpoint: GET /api/v1/practices/{practiceId}/storage/stats/
 */
export function getStorageStats(practiceId) {
    return async () => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/stats/`
            );
            if (res.status === 200) {
                dispatch(slice.actions.setStats(res.data));
            }
            // Stats are nice-to-have — don't dispatch error on failure
        } catch {
            // Silently ignore
        }
    };
}

/**
 * Upload a file manually.
 * Endpoint: POST /api/v1/practices/{practiceId}/storage/files/upload/
 * @param {string} practiceId
 * @param {FormData} formData - file + metadata fields
 * @param {Function} callback
 */
export function uploadFile(practiceId, formData, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/files/upload/`,
                'post',
                formData
            );
            if (res.status === 201 || res.status === 200) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: res.data });
            } else {
                dispatch(slice.actions.hasError(res.data));
                dispatch(updateError({ success: false, message: JSON.stringify(res.data) || 'Failed to upload file' }));
                if (callback) callback({ success: false, data: res.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to upload file' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a file.
 * Endpoint: DELETE /api/v1/practices/{practiceId}/storage/files/{fileId}/
 */
export function deleteFile(practiceId, fileId, callback) {
    return async () => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/files/${fileId}/`,
                'delete'
            );
            if (res.status === 204 || res.status === 200) {
                if (callback) callback({ success: true });
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete file' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete file' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Re-sync a file to cloud storage (retry failed upload).
 * Endpoint: POST /api/v1/practices/{practiceId}/storage/files/{fileId}/resync/
 */
export function resyncFile(practiceId, fileId, callback) {
    return async () => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/files/${fileId}/resync/`,
                'post'
            );
            if (res.status === 200 || res.status === 201) {
                if (callback) callback({ success: true, data: res.data });
            } else {
                dispatch(updateError({ success: false, message: 'Failed to re-sync file' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to re-sync file' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Fetch files scoped to a single lead (for LeadFormsTab).
 * Endpoint: GET /api/v1/practices/{practiceId}/storage/files/?lead={leadId}
 */
export function getLeadFiles(practiceId, leadId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadFilesLoading());
            const sp = new URLSearchParams();
            sp.set('lead', leadId);
            sp.set('ordering', '-created_at');
            sp.set('limit', params.limit || '5');
            if (params.offset !== undefined) sp.set('offset', params.offset);

            const res = await callApi(
                `/api/v1/practices/${practiceId}/storage/files/?${sp.toString()}`
            );
            if (res.status === 200) {
                const { data } = res;
                dispatch(slice.actions.setLeadFiles({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadFiles({ count: 0, results: [] }));
            }
        } catch {
            dispatch(slice.actions.setLeadFiles({ count: 0, results: [] }));
        }
    };
}
